import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Building2, Save, X, Edit, Phone, Facebook, Twitter, Linkedin, Instagram, Globe, Sliders, Type, PlusCircle, Trash2, GripVertical, Image, ChevronUp, ChevronDown, ToggleLeft, ToggleRight, Bell, Eye, EyeOff, Upload } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../context/SettingsContext';
import { useBranding } from '../../context/BrandingContext';
import { switchTenant } from '../../utils/tenantUtils';
import apiService from '../../services/apiService';

const SystemSettingsPage = () => {
  const { isAdmin, isGlobalAdmin, setCurrentTenant, currentTenant } = useAuth();
  const navigate = useNavigate();
  const { settings, updateSettings } = useSettings();
  const { companyBranding, updateBranding, refreshBranding } = useBranding();
  
  const [activeSection, setActiveSection] = useState('companies');
  
  // Configuración de empresas desde branding context con validación
  const [companySettings, setCompanySettings] = useState(companyBranding || {
    chirakChile: {
      name: 'Chirak Turismo Chile',
      primaryColor: '#3B82F6',
      secondaryColor: '#1E40AF',
      logoUrl: ''
    },
    chirakPeru: {
      name: 'Chirak Turismo Perú',
      primaryColor: '#EF4444',
      secondaryColor: '#991B1B',
      logoUrl: ''
    }
  });

  // Actualizar companySettings cuando companyBranding cambie (después de cargar desde API)
  React.useEffect(() => {
    console.log('🔄 SystemSettings - companyBranding cambió:', companyBranding);
    if (companyBranding && Object.keys(companyBranding).length > 0) {
      console.log('✅ Actualizando companySettings con datos de la API');
      setCompanySettings(companyBranding);
    }
  }, [companyBranding]);

  // Configuración general del sistema
  const [generalSettings, setGeneralSettings] = useState({
    systemName: settings.companyName || 'Tarifario Chirak',
    companyLogoUrl: settings.companyLogoUrl || '',
    phone: settings.phone || '',
    email: settings.email || '',
    address: settings.address || '',
    social: {
      facebook: settings.social?.facebook || '',
      twitter: settings.social?.twitter || '',
      linkedin: settings.social?.linkedin || '',
      instagram: settings.social?.instagram || ''
    }
  });

  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  // Estado para sliders
  const [sliders, setSliders] = useState([]);
  const [loadingSliders, setLoadingSliders] = useState(false);
  const [isSliderModalOpen, setIsSliderModalOpen] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);

  // Estado para popups
  const [popupCompanyId, setPopupCompanyId] = useState(null);  // empresa seleccionada
  const [popups, setPopups] = useState([]);
  const [loadingPopups, setLoadingPopups] = useState(false);
  const [savingPopups, setSavingPopups] = useState(false);
  const [isPopupModalOpen, setIsPopupModalOpen] = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [popupSuccessMsg, setPopupSuccessMsg] = useState('');

  const PAGES_OPTIONS = [
    { value: '/', label: 'Inicio (/)' },
    { value: '/categorias', label: 'Categorías' },
    { value: '/productos', label: 'Productos' },
    { value: '/admin', label: 'Panel Admin' },
  ];

  const emptyPopup = () => ({
    id: Date.now(),
    title: '',
    content: '',
    imageUrl: '',
    buttonText: '',
    buttonLink: '',
    active: true,
    trigger: 'always',
    delay: 0,
    showOnPages: ['/']
  });

  // Cargar popups de la empresa seleccionada
  const loadPopups = (companyId) => {
    const data = companyBranding?.[companyId]?.popups || [];
    setPopups(JSON.parse(JSON.stringify(data)));  // copia profunda
  };

  React.useEffect(() => {
    if (activeSection === 'popups') {
      // Si no es global_admin, siempre fija al tenant actual sin posibilidad de cambiar
      const effectiveId = isGlobalAdmin
        ? (popupCompanyId || currentTenant || Object.keys(companyBranding || {})[0] || null)
        : (currentTenant || Object.keys(companyBranding || {})[0] || null);

      if (effectiveId) {
        setPopupCompanyId(effectiveId);
        loadPopups(effectiveId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, companyBranding]);

  const handleSelectPopupCompany = (companyId) => {
    setPopupCompanyId(companyId);
    loadPopups(companyId);
  };

  const handleSavePopups = async (updatedPopups) => {
    if (!popupCompanyId) return;
    try {
      setSavingPopups(true);
      await apiService.updateCompany(popupCompanyId, { popups: updatedPopups });
      setPopups(updatedPopups);
      updateBranding(popupCompanyId, { ...companyBranding?.[popupCompanyId], popups: updatedPopups });
      await refreshBranding();
      setPopupSuccessMsg('Ventanas emergentes guardadas correctamente');
      setTimeout(() => setPopupSuccessMsg(''), 3000);
    } catch (err) {
      alert('Error al guardar popups: ' + err.message);
    } finally {
      setSavingPopups(false);
    }
  };

  const handleTogglePopup = (id) => {
    const updated = popups.map(p => p.id === id ? { ...p, active: !p.active } : p);
    setPopups(updated);
    handleSavePopups(updated);
  };

  const handleDeletePopup = (id) => {
    if (!window.confirm('¿Eliminar esta ventana emergente?')) return;
    const updated = popups.filter(p => p.id !== id);
    setPopups(updated);
    handleSavePopups(updated);
  };

  const handleSavePopup = (popup) => {
    const exists = popups.find(p => p.id === popup.id);
    const updated = exists
      ? popups.map(p => p.id === popup.id ? popup : p)
      : [...popups, popup];
    setPopups(updated);
    handleSavePopups(updated);
    setIsPopupModalOpen(false);
    setEditingPopup(null);
  };

  // Cargar sliders cuando cambie a la sección de slider
  React.useEffect(() => {
    if (activeSection === 'slider') {
      loadSliders();
    }
  }, [activeSection]);

  const loadSliders = async () => {
    try {
      setLoadingSliders(true);
      const data = await apiService.getAllSliders();
      setSliders(data || []);
    } catch (error) {
      console.error('Error cargando sliders:', error);
      setSliders([]);
    } finally {
      setLoadingSliders(false);
    }
  };

  if (!isAdmin) {
    navigate('/admin');
    return null;
  }

  const sectionItems = [
    {
      id: 'companies',
      label: 'Configuración de Empresas',
      icon: Building2,
      description: 'Gestiona información corporativa y branding',
      color: 'blue'
    },
    {
      id: 'general',
      label: 'Configuración General',
      icon: Settings,
      description: 'Información general del sistema y contacto',
      color: 'gray'
    },
    {
      id: 'slider',
      label: 'Slider Informativo',
      icon: Sliders,
      description: 'Configuración del slider de información dinámica',
      color: 'green'
    },
    {
      id: 'popups',
      label: 'Ventanas Emergentes',
      icon: Type,
      description: 'Gestión de popups y notificaciones',
      color: 'purple'
    }
  ];

  const handleEditCompany = (companyId) => {
    setEditingCompany(companyId);
    setIsCompanyModalOpen(true);
  };

  const handleSaveCompany = async (companyId, newSettings) => {
    try {
      // Preparar datos para la API (convertir camelCase a snake_case)
      const dataToSave = {
        name: newSettings.name,
        email: newSettings.email,
        phone: newSettings.phone,
        whatsapp_number: newSettings.whatsapp_number || '',
        address: newSettings.address,
        website: newSettings.website,
        description: newSettings.description,
        logo: newSettings.logo,
        franchise_logo: newSettings.franchise_logo || '',
        franchise_name: newSettings.franchise_name || '',
        footer_text: newSettings.footerText,
        primary_color: newSettings.primaryColor,
        secondary_color: newSettings.secondaryColor,
        button_back_color: newSettings.buttonBackColor || '#10B981',
        button_category_selected_color: newSettings.buttonCategorySelectedColor || '#3B82F6',
        button_download_color: newSettings.buttonDownloadColor || '#2563EB',
        button_marketing_kit_color: newSettings.buttonMarketingKitColor || '#10B981',
        button_hotel_color: newSettings.buttonHotelColor || '#F59E0B',
        button_travel_guide_color: newSettings.buttonTravelGuideColor || '#8B5CF6',
        button_descriptives_color: newSettings.buttonDescriptivesColor || '#F59E0B',
        tutorial_video_url: newSettings.tutorialVideoUrl || '',
        marketing_kit_url: newSettings.marketingKitUrl || '',
        hotel_button_url: newSettings.hotelButtonUrl || '',
        travel_guide_url: newSettings.travelGuideUrl || '',
        descriptives_url: newSettings.descriptivesUrl || '',
        show_language_es: newSettings.showLanguageEs !== undefined ? newSettings.showLanguageEs : true,
        show_language_en: newSettings.showLanguageEn !== undefined ? newSettings.showLanguageEn : true,
        show_language_pt: newSettings.showLanguagePt !== undefined ? newSettings.showLanguagePt : true,
        social_facebook: newSettings.social?.facebook || '',
        social_twitter: newSettings.social?.twitter || '',
        social_instagram: newSettings.social?.instagram || '',
        social_linkedin: newSettings.social?.linkedin || '',
        social_youtube: newSettings.social?.youtube || ''
      };

      console.log('💾 Guardando empresa:', companyId);
      console.log('📱 Número de WhatsApp:', dataToSave.whatsapp_number);

      // Guardar en la base de datos
      const result = await apiService.updateCompany(companyId, dataToSave);
      console.log('✅ Respuesta del servidor:', result);

      // Actualizar estado local
      setCompanySettings({
        ...companySettings,
        [companyId]: newSettings
      });

      // Actualizar branding en tiempo real
      updateBranding(companyId, newSettings);
      
      setIsCompanyModalOpen(false);
      
      alert('Configuración guardada exitosamente. Los cambios se aplicarán al recargar la página.');
      
      // Recargar la página para ver los cambios
      window.location.reload();
    } catch (error) {
      console.error('❌ Error guardando empresa:', error);
      console.error('  - Mensaje:', error.message);
      console.error('  - Stack:', error.stack);
      alert('Error al guardar la configuración: ' + error.message);
    }
  };

  const handleSaveSettings = (e) => {
    e.preventDefault();
    updateSettings({
      ...generalSettings,
      social: generalSettings.social
    });
    alert('Configuración guardada exitosamente');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            <Settings className="inline w-10 h-10 mr-3 text-blue-600" />
            Configuración del Sistema
          </h2>
          <p className="text-xl text-gray-600">Administra todas las configuraciones del sistema multi-empresa.</p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Sidebar de navegación */}
          <div className="lg:col-span-1">
            <motion.div
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-8"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="font-semibold text-gray-800 mb-4">Secciones</h3>
              <nav className="space-y-2">
                {sectionItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
                        activeSection === item.id
                          ? `bg-${item.color}-50 text-${item.color}-700 border border-${item.color}-200`
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        <div>
                          <div className="font-medium text-sm">{item.label}</div>
                          <div className="text-xs opacity-75">{item.description}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </motion.div>
          </div>

          {/* Contenido principal */}
          <div className="lg:col-span-4">
            <motion.div
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              
              {/* Configuración de Empresas */}
              {activeSection === 'companies' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">Configuración de Empresas</h3>
                    <div className="flex flex-col gap-2 items-end">
                      <div className="text-sm text-gray-500">
                        Gestiona información corporativa y branding
                      </div>
                      {process.env.NODE_ENV === 'development' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const newTenant = switchTenant('chirakChile');
                              setCurrentTenant(newTenant);
                            }}
                            className="px-3 py-1 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                          >
                            Vista Chirak Chile
                          </button>
                          <button
                            onClick={() => {
                              const newTenant = switchTenant('chirakPeru');
                              setCurrentTenant(newTenant);
                            }}
                            className="px-3 py-1 text-xs bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                          >
                            Vista Chirak Perú
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-6">
                    {companySettings && Object.entries(companySettings).map(([companyId, companyData]) => (
                      <motion.div 
                        key={companyId} 
                        className="border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                        whileHover={{ y: -2 }}
                      >
                        {/* Header corporativo */}
                        <div 
                          className="p-4 text-white"
                          style={{ backgroundColor: companyData.primaryColor }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <img
                                src={companyData.logo}
                                alt={companyData.name}
                                className="w-12 h-12 rounded-lg bg-white/20 p-1"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                              <div>
                                <h4 className="font-bold text-lg">{companyData.name}</h4>
                                <p className="text-sm opacity-90">{companyData.description}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleEditCompany(companyId)}
                              className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Información compacta */}
                        <div className="p-4">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <h5 className="font-medium text-gray-800 text-sm mb-2">Contacto</h5>
                              <div className="space-y-1 text-xs text-gray-600">
                                <div>{companyData.email}</div>
                                <div>{companyData.phone}</div>
                                <div className="text-blue-600">{companyData.website}</div>
                              </div>
                            </div>
                            <div>
                              <h5 className="font-medium text-gray-800 text-sm mb-2">Branding</h5>
                              <div className="flex gap-2 mb-2">
                                <div 
                                  className="w-4 h-4 rounded border"
                                  style={{ backgroundColor: companyData.primaryColor }}
                                ></div>
                                <div 
                                  className="w-4 h-4 rounded border"
                                  style={{ backgroundColor: companyData.secondaryColor }}
                                ></div>
                              </div>
                              <div className="text-xs text-gray-600 italic">
                                "{companyData.footerText}"
                              </div>
                            </div>
                          </div>
                          
                          {/* Redes Sociales */}
                          {companyData.social && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <h5 className="font-medium text-gray-800 text-sm mb-2">Redes Sociales</h5>
                              <div className="flex gap-2 flex-wrap">
                                {companyData.social.facebook && (
                                  <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">Facebook</span>
                                )}
                                {companyData.social.twitter && (
                                  <span className="text-xs bg-sky-100 text-sky-600 px-2 py-1 rounded">Twitter</span>
                                )}
                                {companyData.social.instagram && (
                                  <span className="text-xs bg-pink-100 text-pink-600 px-2 py-1 rounded">Instagram</span>
                                )}
                                {companyData.social.linkedin && (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">LinkedIn</span>
                                )}
                                {companyData.social.youtube && (
                                  <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">YouTube</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Configuración General */}
              {activeSection === 'general' && (
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Configuración General</h3>
                  
                  <form onSubmit={handleSaveSettings} className="space-y-8">
                    {/* Información de la empresa */}
                    <div className="grid lg:grid-cols-2 gap-8">
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-blue-600" />
                            Información Corporativa
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Sistema</label>
                              <input
                                type="text"
                                value={generalSettings.systemName}
                                onChange={(e) => setGeneralSettings({...generalSettings, systemName: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">URL del Logo</label>
                              <input
                                type="url"
                                value={generalSettings.companyLogoUrl}
                                onChange={(e) => setGeneralSettings({...generalSettings, companyLogoUrl: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-6">
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <Phone className="w-5 h-5 text-green-600" />
                            Información de Contacto
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                              <input
                                type="tel"
                                value={generalSettings.phone}
                                onChange={(e) => setGeneralSettings({...generalSettings, phone: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                              <input
                                type="email"
                                value={generalSettings.email}
                                onChange={(e) => setGeneralSettings({...generalSettings, email: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
                              <input
                                type="text"
                                value={generalSettings.address}
                                onChange={(e) => setGeneralSettings({...generalSettings, address: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Redes sociales en una sola fila */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-purple-600" />
                        Redes Sociales
                      </h4>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Facebook className="inline w-4 h-4 mr-1" />
                            Facebook
                          </label>
                          <input
                            type="url"
                            value={generalSettings.social.facebook}
                            onChange={(e) => setGeneralSettings({
                              ...generalSettings, 
                              social: {...generalSettings.social, facebook: e.target.value}
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Twitter className="inline w-4 h-4 mr-1" />
                            Twitter
                          </label>
                          <input
                            type="url"
                            value={generalSettings.social.twitter}
                            onChange={(e) => setGeneralSettings({
                              ...generalSettings, 
                              social: {...generalSettings.social, twitter: e.target.value}
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Linkedin className="inline w-4 h-4 mr-1" />
                            LinkedIn
                          </label>
                          <input
                            type="url"
                            value={generalSettings.social.linkedin}
                            onChange={(e) => setGeneralSettings({
                              ...generalSettings, 
                              social: {...generalSettings.social, linkedin: e.target.value}
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Instagram className="inline w-4 h-4 mr-1" />
                            Instagram
                          </label>
                          <input
                            type="url"
                            value={generalSettings.social.instagram}
                            onChange={(e) => setGeneralSettings({
                              ...generalSettings, 
                              social: {...generalSettings.social, instagram: e.target.value}
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end pt-6 border-t">
                      <button
                        type="submit"
                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        Guardar Configuración
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Slider Informativo */}
              {activeSection === 'slider' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Slider Informativo</h3>
                      <p className="text-gray-600 mt-1">Gestiona los elementos del slider dinámico por empresa.</p>
                    </div>
                    <button
                      onClick={() => {
                        setEditingSlider(null);
                        setIsSliderModalOpen(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <PlusCircle className="w-5 h-5" />
                      Nuevo Slide
                    </button>
                  </div>

                  {loadingSliders ? (
                    <div className="text-center py-12 text-gray-500">
                      <Sliders className="w-12 h-12 mx-auto mb-4 opacity-50 animate-pulse" />
                      <p>Cargando sliders...</p>
                    </div>
                  ) : sliders.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Sliders className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No hay sliders configurados para esta empresa</p>
                      <button
                        onClick={() => {
                          setEditingSlider(null);
                          setIsSliderModalOpen(true);
                        }}
                        className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Crear primer slide
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sliders.map((slider, index) => (
                        <motion.div
                          key={slider.id}
                          className={`border rounded-lg p-4 ${
                            slider.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-300 opacity-60'
                          }`}
                          layout
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={async () => {
                                  if (index > 0) {
                                    await apiService.reorderSlider(slider.id, slider.order_index - 1);
                                    await loadSliders();
                                  }
                                }}
                                disabled={index === 0}
                                className={`p-1 rounded hover:bg-gray-100 ${index === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <GripVertical className="w-4 h-4 text-gray-400" />
                              <button
                                onClick={async () => {
                                  if (index < sliders.length - 1) {
                                    await apiService.reorderSlider(slider.id, slider.order_index + 1);
                                    await loadSliders();
                                  }
                                }}
                                disabled={index === sliders.length - 1}
                                className={`p-1 rounded hover:bg-gray-100 ${index === sliders.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div
                                  className={`px-3 py-1 rounded-lg bg-gradient-to-r ${slider.color} text-white text-sm font-medium`}
                                >
                                  {slider.icon || 'Sin icono'}
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900">{slider.title}</h4>
                                <span className="text-xs text-gray-500">#{slider.order_index}</span>
                              </div>
                              <p className="text-gray-600 text-sm">{slider.value}</p>
                              {slider.image_url && (
                                <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                                  <Image className="w-4 h-4" />
                                  <span className="truncate max-w-xs">{slider.image_url}</span>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                onClick={async () => {
                                  await apiService.updateSlider(slider.id, { is_active: !slider.is_active });
                                  await loadSliders();
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                title={slider.is_active ? 'Desactivar' : 'Activar'}
                              >
                                {slider.is_active ? (
                                  <ToggleRight className="w-5 h-5 text-green-600" />
                                ) : (
                                  <ToggleLeft className="w-5 h-5 text-gray-400" />
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingSlider(slider);
                                  setIsSliderModalOpen(true);
                                }}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={async () => {
                                  if (window.confirm('¿Seguro que deseas eliminar este slide?')) {
                                    await apiService.deleteSlider(slider.id);
                                    await loadSliders();
                                  }
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Ventanas Emergentes */}
              {activeSection === 'popups' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Ventanas Emergentes</h3>
                      <p className="text-gray-500 text-sm mt-1">Configura popups por empresa. Se muestran al cargar las páginas seleccionadas.</p>
                    </div>
                    <button
                      onClick={() => { setEditingPopup(emptyPopup()); setIsPopupModalOpen(true); }}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <PlusCircle className="w-5 h-5" />
                      Nuevo Popup
                    </button>
                  </div>

                  {/* Selector de empresa — solo visible para global_admin */}
                  {isGlobalAdmin && companyBranding && Object.keys(companyBranding).length > 1 && (
                    <div className="flex gap-2 flex-wrap mt-4 mb-6">
                      {Object.entries(companyBranding).map(([id, c]) => (
                        <button
                          key={id}
                          onClick={() => handleSelectPopupCompany(id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            popupCompanyId === id
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-purple-50'
                          }`}
                        >
                          {c.name || id}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Indicador de empresa para no-global-admin */}
                  {!isGlobalAdmin && popupCompanyId && companyBranding?.[popupCompanyId] && (
                    <div className="mt-4 mb-6 inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700">
                      <Bell className="w-4 h-4" />
                      Empresa: <span className="font-semibold">{companyBranding[popupCompanyId].name || popupCompanyId}</span>
                    </div>
                  )}

                  {popupSuccessMsg && (
                    <div className="mb-4 px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm">{popupSuccessMsg}</div>
                  )}

                  {popups.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                      <Bell className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p>No hay ventanas emergentes configuradas.</p>
                      <button
                        onClick={() => { setEditingPopup(emptyPopup()); setIsPopupModalOpen(true); }}
                        className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                      >
                        Crear primera ventana emergente
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {popups.map((popup) => (
                        <motion.div
                          key={popup.id}
                          layout
                          className={`border rounded-xl p-4 flex items-start gap-4 transition-all ${
                            popup.active ? 'bg-white border-gray-200 shadow-sm' : 'bg-gray-50 border-gray-200 opacity-60'
                          }`}
                        >
                          {/* Preview imagen */}
                          <div className="w-16 h-16 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden border border-gray-200">
                            {popup.imageUrl
                              ? <img src={popup.imageUrl} alt="" className="w-full h-full object-cover" onError={e => e.target.style.display='none'} />
                              : <Bell className="w-8 h-8 m-4 text-purple-300" />
                            }
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-gray-900 truncate">{popup.title || '(Sin título)'}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${popup.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                                {popup.active ? 'Activo' : 'Inactivo'}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                                {popup.trigger === 'once_session' ? '1×/sesión' : popup.trigger === 'once_ever' ? 'Solo 1 vez' : 'Siempre'}
                              </span>
                              {popup.delay > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-100">
                                  ⏱ {popup.delay}s
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 truncate mt-0.5">{popup.content}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {(popup.showOnPages || []).map(page => (
                                <span key={page} className="text-xs bg-purple-50 text-purple-700 border border-purple-100 px-2 py-0.5 rounded">{page}</span>
                              ))}
                            </div>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleTogglePopup(popup.id)}
                              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                              title={popup.active ? 'Desactivar' : 'Activar'}
                            >
                              {popup.active
                                ? <ToggleRight className="w-5 h-5 text-green-600" />
                                : <ToggleLeft className="w-5 h-5 text-gray-400" />
                              }
                            </button>
                            <button
                              onClick={() => { setEditingPopup({ ...popup }); setIsPopupModalOpen(true); }}
                              className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeletePopup(popup.id)}
                              className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </motion.div>
          </div>
        </div>

        {/* Modal para editar empresa */}
        <CompanyModal 
          isOpen={isCompanyModalOpen}
          onClose={() => setIsCompanyModalOpen(false)}
          companyId={editingCompany}
          settings={editingCompany ? companySettings[editingCompany] : null}
          onSave={handleSaveCompany}
        />

        {/* Modal para editar slider */}
        <SliderModal
          isOpen={isSliderModalOpen}
          onClose={() => {
            setIsSliderModalOpen(false);
            setEditingSlider(null);
          }}
          slider={editingSlider}
          onSave={async (sliderData) => {
            try {
              if (editingSlider) {
                await apiService.updateSlider(editingSlider.id, sliderData);
              } else {
                await apiService.createSlider(sliderData);
              }
              await loadSliders();
              setIsSliderModalOpen(false);
              setEditingSlider(null);
            } catch (error) {
              console.error('Error guardando slider:', error);
              alert('Error al guardar el slider: ' + error.message);
            }
          }}
        />

        {/* Modal para editar popup */}
        <PopupModal
          isOpen={isPopupModalOpen}
          onClose={() => { setIsPopupModalOpen(false); setEditingPopup(null); }}
          popup={editingPopup}
          pagesOptions={PAGES_OPTIONS}
          onSave={handleSavePopup}
          saving={savingPopups}
        />
      </div>
    </div>
  );
};

// Componente Modal para editar empresa
const CompanyModal = ({ isOpen, onClose, companyId, settings, onSave }) => {
  const [formData, setFormData] = useState(settings || {});
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFranchiseLogo, setUploadingFranchiseLogo] = useState(false);
  const fileInputRef = React.useRef(null);
  const franchiseFileInputRef = React.useRef(null);

  React.useEffect(() => {
    // Solo actualizar formData cuando el modal se abre, no cuando settings cambia
    if (isOpen && settings && Object.keys(settings).length > 0) {
      setFormData(settings);
    }
  }, [companyId, isOpen]); // Removido 'settings' de las dependencias

  const handleSave = () => {
    if (companyId) {
      onSave(companyId, formData);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'social') {
      setFormData({ 
        ...formData, 
        social: {
          ...formData.social,
          ...value
        }
      });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 5MB');
      return;
    }

    setUploadingLogo(true);

    try {
      console.log('📤 Iniciando upload de logo con apiService...');
      const result = await apiService.uploadFile(file, 'logos');
      console.log('✅ Logo subido exitosamente:', result);
      
      // Actualizar el campo logo con la URL devuelta
      handleInputChange('logo', result.url);
      alert('Logo subido exitosamente. Recuerda hacer clic en "Guardar" para aplicar los cambios.');
    } catch (error) {
      console.error('❌ Error uploading logo:', error);
      alert('Error al subir el logo: ' + error.message);
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFranchiseLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 5MB');
      return;
    }

    setUploadingFranchiseLogo(true);

    try {
      console.log('📤 Iniciando upload de logo de franquicia...');
      const result = await apiService.uploadFile(file, 'logos');
      console.log('✅ Logo de franquicia subido exitosamente:', result);
      
      // Actualizar el campo franchise_logo con la URL devuelta
      handleInputChange('franchise_logo', result.url);
      alert('Logo de franquicia subido exitosamente. Recuerda hacer clic en "Guardar" para aplicar los cambios.');
    } catch (error) {
      console.error('❌ Error uploading franchise logo:', error);
      alert('Error al subir el logo de franquicia: ' + error.message);
    } finally {
      setUploadingFranchiseLogo(false);
      if (franchiseFileInputRef.current) {
        franchiseFileInputRef.current.value = '';
      }
    }
  };

  const getLogoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${apiService.baseURL}${url}`;
  };

  if (!isOpen || !settings) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">
              Editar {formData.name}
            </h3>
            <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo de la Empresa</label>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {formData.logo ? (
                    <img
                      src={getLogoUrl(formData.logo)}
                      alt="Logo"
                      className="w-24 h-24 object-contain border border-gray-300 rounded-lg bg-white p-2"
                      onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=200'}
                    />
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400">
                      <Building2 className="w-8 h-8" />
                    </div>
                  )}
                </div>

                <div className="flex-1 space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                      uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {uploadingLogo ? 'Subiendo...' : 'Seleccionar Logo'}
                  </label>
                  <input
                    type="text"
                    value={formData.logo || ''}
                    onChange={(e) => handleInputChange('logo', e.target.value)}
                    placeholder="O ingresa URL del logo"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <p className="text-xs text-gray-500">Sube una imagen o ingresa la URL. Máximo 5MB.</p>
                </div>
              </div>
            </div>

            {/* Logo de Franquicia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo de Franquicia (Opcional)</label>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {formData.franchise_logo ? (
                    <img
                      src={getLogoUrl(formData.franchise_logo)}
                      alt="Logo Franquicia"
                      className="w-24 h-24 object-contain border border-gray-300 rounded-lg bg-white p-2"
                      onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=200'}
                    />
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400">
                      <Building2 className="w-8 h-8" />
                    </div>
                  )}
                </div>

                <div className="flex-1 space-y-2">
                  <input
                    ref={franchiseFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFranchiseLogoUpload}
                    className="hidden"
                    id="franchise-logo-upload"
                  />
                  <label
                    htmlFor="franchise-logo-upload"
                    className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                      uploadingFranchiseLogo ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {uploadingFranchiseLogo ? 'Subiendo...' : 'Seleccionar Logo Franquicia'}
                  </label>
                  <input
                    type="text"
                    value={formData.franchise_logo || ''}
                    onChange={(e) => handleInputChange('franchise_logo', e.target.value)}
                    placeholder="O ingresa URL del logo de franquicia"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <p className="text-xs text-gray-500">Sube una imagen o ingresa la URL. Este logo aparecerá junto al logo principal en el header. Máximo 5MB.</p>
                </div>
              </div>
            </div>

            {/* Nombre de Franquicia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de Franquicia (Opcional)</label>
              <input
                type="text"
                value={formData.franchise_name || ''}
                onChange={(e) => handleInputChange('franchise_name', e.target.value)}
                placeholder="Ej: Powered by Travel Agency"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Nombre que aparecerá junto al logo de franquicia</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+56 9 1234 5678"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp
                  <span className="text-xs text-gray-500 ml-2">(sin símbolos)</span>
                </label>
                <input
                  type="tel"
                  value={formData.whatsapp_number || ''}
                  onChange={(e) => handleInputChange('whatsapp_number', e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="56912345678"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Ej: 56912345678 (código país + número)</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                <input
                  type="url"
                  value={formData.website || ''}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div></div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Texto del Footer</label>
              <input
                type="text"
                value={formData.footerText || ''}
                onChange={(e) => handleInputChange('footerText', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Redes Sociales */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Redes Sociales</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facebook</label>
                  <input
                    type="url"
                    value={formData.social?.facebook || ''}
                    onChange={(e) => handleInputChange('social', { ...formData.social, facebook: e.target.value })}
                    placeholder="https://facebook.com/empresa"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Twitter</label>
                  <input
                    type="url"
                    value={formData.social?.twitter || ''}
                    onChange={(e) => handleInputChange('social', { ...formData.social, twitter: e.target.value })}
                    placeholder="https://twitter.com/empresa"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instagram</label>
                  <input
                    type="url"
                    value={formData.social?.instagram || ''}
                    onChange={(e) => handleInputChange('social', { ...formData.social, instagram: e.target.value })}
                    placeholder="https://instagram.com/empresa"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn</label>
                  <input
                    type="url"
                    value={formData.social?.linkedin || ''}
                    onChange={(e) => handleInputChange('social', { ...formData.social, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/company/empresa"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">YouTube</label>
                  <input
                    type="url"
                    value={formData.social?.youtube || ''}
                    onChange={(e) => handleInputChange('social', { ...formData.social, youtube: e.target.value })}
                    placeholder="https://youtube.com/@empresa"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Configuración de Idiomas */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                Idiomas Disponibles
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Controla qué botones de idioma se muestran en el sistema (Login y Dashboard)
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <img src="/flags/es.svg" alt="Español" className="w-8 h-8 rounded-full" />
                    <div>
                      <div className="font-medium text-gray-900">Español</div>
                      <div className="text-xs text-gray-500">Spanish</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInputChange('showLanguageEs', !formData.showLanguageEs)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.showLanguageEs ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.showLanguageEs ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <img src="/flags/us.svg" alt="English" className="w-8 h-8 rounded-full" />
                    <div>
                      <div className="font-medium text-gray-900">English</div>
                      <div className="text-xs text-gray-500">Inglés</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInputChange('showLanguageEn', !formData.showLanguageEn)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.showLanguageEn ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.showLanguageEn ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <img src="/flags/br.svg" alt="Português" className="w-8 h-8 rounded-full" />
                    <div>
                      <div className="font-medium text-gray-900">Português</div>
                      <div className="text-xs text-gray-500">Portuguese</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInputChange('showLanguagePt', !formData.showLanguagePt)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.showLanguagePt ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.showLanguagePt ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color Principal</label>
                <input
                  type="color"
                  value={formData.primaryColor || '#3B82F6'}
                  onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                  className="w-full h-10 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color Secundario</label>
                <input
                  type="color"
                  value={formData.secondaryColor || '#1E40AF'}
                  onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                  className="w-full h-10 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Colores de Botones</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Botón Volver</label>
                  <input
                    type="color"
                    value={formData.buttonBackColor || '#10B981'}
                    onChange={(e) => handleInputChange('buttonBackColor', e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Categoría Seleccionada</label>
                  <input
                    type="color"
                    value={formData.buttonCategorySelectedColor || '#3B82F6'}
                    onChange={(e) => handleInputChange('buttonCategorySelectedColor', e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Botón Descargar Productos</label>
                  <input
                    type="color"
                    value={formData.buttonDownloadColor || '#2563EB'}
                    onChange={(e) => handleInputChange('buttonDownloadColor', e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Botón Marketing Kit</label>
                  <input
                    type="color"
                    value={formData.buttonMarketingKitColor || '#10B981'}
                    onChange={(e) => handleInputChange('buttonMarketingKitColor', e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Botón Reserva Hotel</label>
                  <input
                    type="color"
                    value={formData.buttonHotelColor || '#F59E0B'}
                    onChange={(e) => handleInputChange('buttonHotelColor', e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Botón Guía de Viajes</label>
                  <input
                    type="color"
                    value={formData.buttonTravelGuideColor || '#8B5CF6'}
                    onChange={(e) => handleInputChange('buttonTravelGuideColor', e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Botón Descriptivos</label>
                  <input
                    type="color"
                    value={formData.buttonDescriptivesColor || '#F59E0B'}
                    onChange={(e) => handleInputChange('buttonDescriptivesColor', e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* URLs Especiales */}
            <div className="border-t pt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">URLs de Botones Especiales</h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Video Tutorial</label>
                  <input
                    type="url"
                    value={formData.tutorialVideoUrl || ''}
                    onChange={(e) => handleInputChange('tutorialVideoUrl', e.target.value)}
                    placeholder="https://youtube.com/watch?v=..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL del video tutorial que aparece en login</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Marketing Kit</label>
                  <input
                    type="url"
                    value={formData.marketingKitUrl || ''}
                    onChange={(e) => handleInputChange('marketingKitUrl', e.target.value)}
                    placeholder="https://drive.google.com/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL del Marketing Kit que aparece en el sidebar</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reserva de Hotel</label>
                  <input
                    type="url"
                    value={formData.hotelButtonUrl || ''}
                    onChange={(e) => handleInputChange('hotelButtonUrl', e.target.value)}
                    placeholder="https://reservas.hotel.com/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL del botón de reserva de hotel</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Guía de Viajes</label>
                  <input
                    type="url"
                    value={formData.travelGuideUrl || ''}
                    onChange={(e) => handleInputChange('travelGuideUrl', e.target.value)}
                    placeholder="https://guia.viajes.com/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL del botón de Guía de Viajes que aparece en el sidebar</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Descriptivos</label>
                  <input
                    type="url"
                    value={formData.descriptivesUrl || ''}
                    onChange={(e) => handleInputChange('descriptivesUrl', e.target.value)}
                    placeholder="https://descriptivos.com/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL del botón de Descriptivos que aparece en el sidebar</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Guardar
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Componente Modal para editar slider
const SliderModal = ({ isOpen, onClose, slider, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    value: '',
    icon: 'Info',
    color: 'from-blue-500 to-blue-600',
    image_url: '',
    order_index: 0,
    is_active: true
  });

  React.useEffect(() => {
    if (isOpen && slider) {
      setFormData({
        title: slider.title || '',
        value: slider.value || '',
        icon: slider.icon || 'Info',
        color: slider.color || 'from-blue-500 to-blue-600',
        image_url: slider.image_url || '',
        order_index: slider.order_index || 0,
        is_active: slider.is_active !== undefined ? slider.is_active : true
      });
    } else if (isOpen && !slider) {
      setFormData({
        title: '',
        value: '',
        icon: 'Info',
        color: 'from-blue-500 to-blue-600',
        image_url: '',
        order_index: 0,
        is_active: true
      });
    }
  }, [isOpen, slider]);

  const handleSave = () => {
    if (!formData.title) {
      alert('El título es requerido');
      return;
    }
    onSave(formData);
  };

  const colorOptions = [
    { label: 'Azul', value: 'from-blue-500 to-blue-600' },
    { label: 'Verde', value: 'from-green-500 to-green-600' },
    { label: 'Rojo', value: 'from-red-500 to-red-600' },
    { label: 'Amarillo', value: 'from-yellow-500 to-yellow-600' },
    { label: 'Púrpura', value: 'from-purple-500 to-purple-600' },
    { label: 'Rosa', value: 'from-pink-500 to-pink-600' },
    { label: 'Naranja', value: 'from-orange-500 to-orange-600' },
    { label: 'Índigo', value: 'from-indigo-500 to-indigo-600' },
    { label: 'Rosa-Rojo', value: 'from-pink-500 to-red-500' },
    { label: 'Cyan', value: 'from-cyan-500 to-cyan-600' },
  ];

  const iconOptions = [
    'Package', 'Star', 'Users', 'TrendingUp', 'Folder', 'MapPin', 
    'Compass', 'Award', 'Heart', 'Info', 'Zap', 'Clock', 
    'Calendar', 'Globe', 'Shield', 'Gift', 'Target', 'Rocket'
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">
              {slider ? 'Editar Slide' : 'Nuevo Slide'}
            </h3>
            <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ej: Hoteles Disponibles"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descripción / Valor</label>
              <textarea
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="Texto descriptivo del slide"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icono</label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Sin icono</option>
                  {iconOptions.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color del Gradiente</label>
                <select
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {colorOptions.map(color => (
                    <option key={color.value} value={color.value}>{color.label}</option>
                  ))}
                </select>
                <div className={`mt-2 h-8 rounded-lg bg-gradient-to-r ${formData.color}`}></div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">URL de Imagen (opcional)</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://ejemplo.com/imagen.jpg"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {formData.image_url && (
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="mt-2 w-full h-32 object-cover rounded-lg"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Orden</label>
                <input
                  type="number"
                  value={formData.order_index}
                  onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                <button
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  className={`w-full px-3 py-2 rounded-lg font-medium transition-colors ${
                    formData.is_active 
                      ? 'bg-green-100 text-green-700 border border-green-300' 
                      : 'bg-gray-100 text-gray-700 border border-gray-300'
                  }`}
                >
                  {formData.is_active ? '✓ Activo' : '✗ Inactivo'}
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {slider ? 'Guardar Cambios' : 'Crear Slide'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Componente Modal para crear/editar popup
const PopupModal = ({ isOpen, onClose, popup, pagesOptions, onSave, saving }) => {
  const [form, setForm] = useState(popup || {});
  const [customPage, setCustomPage] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = React.useRef(null);

  React.useEffect(() => {
    if (isOpen && popup) setForm({ ...popup });
  }, [isOpen, popup]);

  if (!isOpen || !form) return null;

  const handlePageToggle = (page) => {
    const current = form.showOnPages || [];
    const updated = current.includes(page)
      ? current.filter(p => p !== page)
      : [...current, page];
    setForm({ ...form, showOnPages: updated });
  };

  const handleAddCustomPage = () => {
    const p = customPage.trim();
    if (!p || (form.showOnPages || []).includes(p)) return;
    setForm({ ...form, showOnPages: [...(form.showOnPages || []), p] });
    setCustomPage('');
  };

  const handleRemoveCustomPage = (page) => {
    setForm({ ...form, showOnPages: (form.showOnPages || []).filter(p => p !== page) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title?.trim()) { alert('El título es requerido'); return; }
    onSave(form);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingImage(true);
      const result = await apiService.uploadFile(file, 'popups');
      const fullUrl = result.url.startsWith('http')
        ? result.url
        : `${apiService.baseURL}${result.url}`;
      setForm(prev => ({ ...prev, imageUrl: fullUrl }));
    } catch (err) {
      alert('Error al subir la imagen: ' + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-lg font-bold text-gray-900">
              {popup?.title ? 'Editar Ventana Emergente' : 'Nueva Ventana Emergente'}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Estado activo */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <span className="text-sm font-medium text-gray-700">Estado del popup</span>
              <button
                type="button"
                onClick={() => setForm({ ...form, active: !form.active })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.active ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.active ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className={`text-xs font-medium ${form.active ? 'text-green-600' : 'text-gray-400'}`}>
                {form.active ? 'Activo' : 'Inactivo'}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
              <input
                type="text"
                value={form.title || ''}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="¡Nueva temporada de viajes!"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mensaje</label>
              <textarea
                value={form.content || ''}
                onChange={e => setForm({ ...form, content: e.target.value })}
                placeholder="Descubre los mejores destinos con ofertas exclusivas..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Imagen (opcional)</label>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
              {form.imageUrl ? (
                <div className="relative">
                  <img
                    src={form.imageUrl}
                    alt="preview"
                    className="mt-1 h-28 w-full object-cover rounded-lg border border-gray-200"
                    onError={e => e.target.style.display = 'none'}
                  />
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, imageUrl: '' }))}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow"
                    title="Eliminar imagen"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg py-6 text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-colors text-sm"
                >
                  {uploadingImage ? (
                    <span className="animate-pulse">Subiendo...</span>
                  ) : (
                    <><Upload className="w-4 h-4" /> Subir imagen desde tu equipo</>
                  )}
                </button>
              )}
              {form.imageUrl && (
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="mt-1 text-xs text-purple-600 hover:underline"
                >
                  {uploadingImage ? 'Subiendo...' : 'Cambiar imagen'}
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Texto del botón</label>
                <input
                  type="text"
                  value={form.buttonText || ''}
                  onChange={e => setForm({ ...form, buttonText: e.target.value })}
                  placeholder="Ver más"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Enlace del botón</label>
                <input
                  type="text"
                  value={form.buttonLink || ''}
                  onChange={e => setForm({ ...form, buttonLink: e.target.value })}
                  placeholder="/ o https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>
            </div>

            {/* Frecuencia y Delay */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frecuencia de aparición</label>
                <select
                  value={form.trigger || 'always'}
                  onChange={e => setForm({ ...form, trigger: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                >
                  <option value="always">Siempre (en cada visita)</option>
                  <option value="once_session">Una vez por sesión</option>
                  <option value="once_ever">Solo la primera vez</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Espera antes de aparecer
                  <span className="ml-1 text-gray-400 font-normal">(0 = inmediato)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="60"
                    value={form.delay ?? 0}
                    onChange={e => setForm({ ...form, delay: Math.max(0, parseInt(e.target.value) || 0) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                  <span className="text-sm text-gray-500 whitespace-nowrap">segundos</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">Recomendado: 3–5 seg. da tiempo al usuario de ver la página antes de que aparezca</p>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
              <strong>¿Cuándo aparece?</strong> El popup se mostrará en las páginas seleccionadas
              después de los segundos de espera configurados.{' '}
              {(form.trigger || 'always') === 'once_session' && 'Se mostrará una sola vez por sesión (se reinicia al cerrar el navegador).'}
              {(form.trigger || 'always') === 'once_ever' && 'Se mostrará únicamente la primera vez que el usuario visite la página (nunca más).'}
              {(form.trigger || 'always') === 'always' && 'Aparecerá cada vez que el usuario recargue o visite la página.'}
            </div>

            {/* Páginas donde mostrar */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mostrar en páginas</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {pagesOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handlePageToggle(opt.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      (form.showOnPages || []).includes(opt.value)
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-purple-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {/* Páginas custom ya añadidas (que no están en opciones predefinidas) */}
              {(form.showOnPages || []).filter(p => !pagesOptions.find(o => o.value === p)).map(page => (
                <div key={page} className="inline-flex items-center gap-1 mr-2 mb-2 px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs">
                  {page}
                  <button type="button" onClick={() => handleRemoveCustomPage(page)} className="ml-1 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  value={customPage}
                  onChange={e => setCustomPage(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddCustomPage())}
                  placeholder="Ruta personalizada, ej: /paquetes"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
                />
                <button type="button" onClick={handleAddCustomPage} className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm">
                  Agregar
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {saving ? 'Guardando...' : <><Save className="w-4 h-4" /> Guardar</>}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SystemSettingsPage;